import Head from 'next/head';
import InfiniteScroll from 'common/InfiniteScroll';
import {
  Meta,
  fetchHomePageWidgets,
  Widget,
  WidgetBase, WidgetContinueCarousel, FetchPersonalWidgets,
} from 'common/apiService';
import * as Sentry from '@sentry/nextjs';
import Layout from 'common/Layout/Layout';
import WidgetManager from 'common/WidgetManager';
import usePagination from 'common/usePagination';
import { __t } from 'common/i18n';
import WidgetLoading from 'common/WidgetsLoading';
import Slider from 'common/Slider';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useUser } from '../common/CacheProvider';
import xIPCountry from 'common/apiService/xIPCountry';

type HomePageProps = {
  initialData: { data: Widget[], meta: Meta, },
  initialUrl: string,
};

const sliderRatio = 9 / 16;
const bottomOffsetPercentage = {
  sm: 16,
  md: 26,
  xl: 30,
};

const SliderWrapper = styled.div(({ theme }) => ({
  marginBottom: theme.spacing(8),
  ...theme.mixins.create(bottomOffsetPercentage, (b) => ({ marginBottom: `-${b * sliderRatio}%` })),
}));

export default function HomePage({ initialData, initialUrl }: HomePageProps) {
  const [newData, setNewData] = useState<WidgetBase<'continue_watching', WidgetContinueCarousel> | any>();
  const { authorized, loaded } = useUser();
  useEffect(() => {
    if (authorized && loaded) {
      handlePersonalHomePageWidgets();
    }
  }, [authorized, loaded]);
  const handlePersonalHomePageWidgets = async () => {
    const data = await FetchPersonalWidgets();
    data.data && setNewData(data.data[0]);
  };
  const { data: widgets, getInfiniteScrollProps, hasMore } = usePagination({ initialData, initialUrl });
  const slider = widgets.find(({ type }) => type === 'main_slider') as Widget<'main_slider'>;
  const newWidgets:Widget[] = newData ? [
    ...widgets.slice(0, 2),
    newData,
    ...widgets.slice(2),
  ] : [...widgets];
  return (
    <>
      <Head>
        <title>{__t('siteName')} | {__t('watch.movies.series')}</title>
        <link rel="canonical" href='https://filmnet.ir' />
      </Head>

      <SliderWrapper>
        {slider && <Slider items={slider.configuration.items} serverTime={initialData.meta.server_date_time} />}
      </SliderWrapper>

      <Layout disableTopGutter disableFooter={hasMore}>
        {newWidgets.length > 0 && (
          <InfiniteScroll {...getInfiniteScrollProps()} loader={<WidgetLoading />}>
            <WidgetManager widgets={newWidgets}/>
          </InfiniteScroll>
        )}
      </Layout>
    </>
  );
}

const filterWidgetItems = (widget: Widget) => {
  widget?.configuration?.items?.splice(15);
  return widget;
};

export async function getServerSideProps({ req, res }) {
  xIPCountry({ req, res });

  const count = 5;
  const offset = 0;
  return await fetchHomePageWidgets({ count, offset })
    .then(({ data, meta }) => {
      return {
        props: {
          initialData: { data: data.map(filterWidgetItems), meta },
          initialUrl: `/widgets/home-page?count=${count}`,
        },
      };
    }).catch((err) => {
      Sentry.withScope((scope) => {
        scope.setTag('Status', err.response.status);
        scope.setTag('StatusText', err.response.statusText);
        scope.setTag('Url', err.response.config.url);
        Sentry.captureException(err.response);
      });
      throw new Error(err);
    });
}
